import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface PublicOrderItem {
  productId: number
  name: string
  quantity: number
  price: string
  category: {
    id: number
    name: string
  }
  images: string[]
}

export interface IPublicOrder {
  items: PublicOrderItem[]
  totalPrice: number
  isLoading: boolean
  error: string | null
}

const initialState: IPublicOrder = {
  items: [],
  totalPrice: 0,
  isLoading: false,
  error: null,
}

export const publicOrderSlice = createSlice({
  name: 'publicOrder',
  initialState,
  reducers: {
    addProductToOrder: (state, action: PayloadAction<{ product: PublicOrderItem }>) => {
      const existingItem = state.items.find(
        (item) => item.productId === action.payload.product.productId,
      )

      if (existingItem) {
        existingItem.quantity += 1
      } else {
        state.items.push(action.payload.product)
      }

      state.totalPrice = state.items.reduce(
        (total, item) => total + Number(item.price) * item.quantity,
        0,
      )
    },

    removeProductFromOrder: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter((item) => item.productId !== action.payload)
      state.totalPrice = state.items.reduce(
        (total, item) => total + Number(item.price) * item.quantity,
        0,
      )
    },

    updateProductQuantity: (
      state,
      action: PayloadAction<{ productId: number; quantity: number }>,
    ) => {
      const { productId, quantity } = action.payload
      const item = state.items.find((item) => item.productId === productId)

      if (item) {
        if (quantity <= 0) {
          state.items = state.items.filter((item) => item.productId !== productId)
        } else {
          item.quantity = quantity
        }

        state.totalPrice = state.items.reduce(
          (total, item) => total + Number(item.price) * item.quantity,
          0,
        )
      }
    },

    clearOrder: (state) => {
      state.items = []
      state.totalPrice = 0
    },

    setOrderError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },

    clearOrderError: (state) => {
      state.error = null
    },
  },
})

export const {
  addProductToOrder,
  removeProductFromOrder,
  updateProductQuantity,
  clearOrder,
  setOrderError,
  clearOrderError,
} = publicOrderSlice.actions

export const publicOrderReducer = publicOrderSlice.reducer
